// @flow

import * as React from 'react';
import {Helmet} from 'react-helmet-async';
import {useNavigation, useLocation} from 'docs/data';
import {getSearchResultBreadcrumbs} from 'docs/util';
import type {Article} from 'docs/data';

// This is metadata which is scraped by both the support site search index
// (Opus::Site::Search::DocsPageScraper) and the docs Algolia crawler.
// Also see docs/scripts/algolia/crawler.js
export const SearchIndexingMetadata = ({article}: {article: Article}) => {
  const {navigation} = useNavigation();
  const {location} = useLocation();
  const activeTopic = navigation.topics.find((t) => t.active);
  const activeProduct = navigation.products.find((p) => p.active);

  const breadcrumbs = getSearchResultBreadcrumbs(navigation, article);
  const breadcrumbMetaTags = breadcrumbs.map((text, i) => {
    return (
      <meta
        key={`breadcrumb${i}`}
        // Add 1 to the level since level-0 will be added by the crawler to
        // differentiate between doc pages and apiRef pages
        level={i + 1}
        name={`algolia:hierachy`}
        content={text}
      />
    );
  });

  // depth is used as a ranking criterion in the Algolia search results,
  // it is how many levels deep a doc is in the navigation structure
  let depth = breadcrumbs.length;
  if (!activeTopic && !activeProduct) {
    // If there is no active topic or active product this means that
    // this a standalone doc not in the navigation structure, so set the
    // depth to be higher
    depth = 5;
  }

  const searchTags = article.metadata.search?.tags || [];
  const subtitle = article.metadata.subtitle;

  if (article.metadata.search?.index === false) {
    // This informs crawlers and google bot to ignore indexing this page
    // https://developers.google.com/search/docs/advanced/crawling/block-indexing
    return (
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
    );
  }

  return (
    <Helmet>
      {subtitle && <meta name="description" content={subtitle} />}
      <meta name="algolia:title" content={article.metadata.title} />;
      {subtitle && <meta name="algolia:subtitle" content={subtitle} />}
      <meta name="algolia:depth" content={depth} />;
      <meta name="algolia:article-format" content={article.format} />;
      <link
        rel="canonical"
        href={new URL(article.path, location.baseUrl).toString()}
      />
      {breadcrumbMetaTags}
      {searchTags.map((text, i) => {
        return (
          <meta
            key={`searchTag${i}`}
            name={`algolia:search-tag`}
            content={text}
          />
        );
      })}
    </Helmet>
  );
};
